/**
 * What an onboarding lead's working sheet shows and writes (PLAN-ENCRE-OUTILS O3 bis): its status
 * and type in words, who to reach, its stage as the onboarding's back says it, the refusals of the
 * gated stage writers said in French, and the changes an edit makes — only what changed. Pure.
 */
import { LEAD_STATUS_CONFIG, LEAD_TYPE_CONFIG } from '../../constants/statusConfigs';
import type { Tables } from '../../types/supabase';

export type LeadRow = Tables<'onboarding_leads'>;

/** The lead's full page (`CompanyRoute` keeps a lead's id on it). */
export const leadFicheHref = (id: string): string => `/admin/crm/company/${id}`;

/** The statuses one may set, in the pipeline's order (`completed` is set by the back only). */
export const LEAD_STATUSES = Object.keys(LEAD_STATUS_CONFIG);

/** A status in words, never its key (`completed`, set by the back, included). */
export function leadStatusLabel(status: string | null | undefined): string {
  if (!status) return 'Sans statut';
  if (status === 'completed') return 'Terminé';
  return LEAD_STATUS_CONFIG[status]?.label ?? status;
}

export const LEAD_TYPES = Object.keys(LEAD_TYPE_CONFIG);

export function leadTypeLabel(type: string | null | undefined): string | null {
  return type ? (LEAD_TYPE_CONFIG[type]?.label ?? type) : null;
}

/** « Sandrine Delmas », or null. */
export function leadContactName(lead: Pick<LeadRow, 'contact_first_name' | 'contact_last_name'>): string | null {
  return `${lead.contact_first_name ?? ''} ${lead.contact_last_name ?? ''}`.trim() || null;
}

/** « 12 rue des Lilas, 59000 Lille, France » (France said only when the address is elsewhere). */
export function leadAddress(lead: Pick<LeadRow, 'address' | 'postal_code' | 'city' | 'country'>): string | null {
  const town = [lead.postal_code, lead.city].filter((v) => v?.trim()).join(' ');
  const country = lead.country?.trim() && !/^france$/iu.test(lead.country.trim()) ? lead.country.trim() : null;
  return [lead.address?.trim(), town, country].filter(Boolean).join(', ') || null;
}

/** The ERP said once: the one typed when « other », else the one picked. */
export function leadErp(lead: Pick<LeadRow, 'erp' | 'erp_other'>): string | null {
  if (lead.erp === 'other') return lead.erp_other?.trim() || 'Autre';
  return lead.erp?.trim() || null;
}

/** An exit criterion of the current stage, as `get_onboarding_pipeline` gives it. */
export interface StageCriterion {
  code: string;
  label: string;
  required: boolean;
  met: boolean;
}

/** The lead's onboarding as the back reads it (`get_onboarding_pipeline(p_lead_id)`). */
export interface LeadOnboarding {
  stage: number;
  stageName: string;
  since: string | null;
  criteria: StageCriterion[];
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** The one lead of a `get_onboarding_pipeline(p_lead_id)` answer, or null when it says nothing usable. */
export function onboardingOf(answer: unknown): LeadOnboarding | null {
  const entry = Array.isArray(answer) ? answer[0] : isRecord(answer) && Array.isArray(answer.leads) ? answer.leads[0] : answer;
  if (!isRecord(entry) || typeof entry.stage !== 'number') return null;
  const criteria = Array.isArray(entry.exit_criteria) ? entry.exit_criteria.filter(isRecord) : [];
  return {
    stage: entry.stage,
    stageName: typeof entry.stage_name === 'string' ? entry.stage_name : '',
    since: typeof entry.stage_since === 'string' ? entry.stage_since : null,
    criteria: criteria.map((c) => ({
      code: String(c.code ?? ''),
      label: String(c.label ?? c.code ?? ''),
      required: c.required !== false,
      met: c.met === true,
    })),
  };
}

/**
 * A refusal of the lead's writers said in French: the criteria still missing by their labels (Otto
 * records them), the stage that moved meanwhile, the reason too short, a mail already taken.
 */
export function stageRefusal(message: string): string {
  const missing = /not complete:\s*(\[.*\])\s*$/su.exec(message);
  if (missing) {
    try {
      const list = JSON.parse(missing[1]) as { label?: string; code?: string }[];
      const labels = list.map((c) => c.label ?? c.code).filter(Boolean);
      if (labels.length > 0) return `Il manque encore : ${labels.join(' ; ')}. Otto les enregistre avec leur preuve : demande-le-lui.`;
    } catch {
      // Not the expected list: the message is said as it came.
    }
  }
  if (/^Lead is at stage/u.test(message)) return "L'étape a changé entre-temps : la fiche est relue, réessaie.";
  if (/No exit criterion defined/u.test(message)) return "Aucun critère de sortie n'est défini pour cette étape : on ne peut pas l'avancer d'ici.";
  if (/last one/u.test(message)) return "C'est la dernière étape : l'onboarding est terminé.";
  if (/20 characters/u.test(message)) return 'Le motif doit faire au moins 20 caractères.';
  if (/Can only go back/u.test(message)) return "On ne peut revenir qu'à une étape antérieure : la fiche est relue.";
  if (/Unknown lead/u.test(message)) return "Ce lead n'existe plus.";
  if (/admin required|42501|permission denied/iu.test(message)) return 'Réservé aux administrateurs.';
  if (/duplicate key|unique constraint/iu.test(message) && /contact_email/u.test(message)) return "Ce mail est déjà celui d'un autre lead.";
  return message;
}

/** « Modifier » a lead: the fields its form holds, as typed. */
export interface LeadForm {
  company_name: string;
  siret: string;
  address: string;
  postal_code: string;
  city: string;
  country: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_email: string;
  contact_phone: string;
  erp: string;
  erp_other: string;
  onboarding_type: string;
  status: string;
  internal_notes: string;
}

export type LeadPatch = Partial<Omit<LeadForm, 'erp_other' | 'onboarding_type' | 'internal_notes'>> & {
  erp_other?: string | null;
  onboarding_type?: string | null;
  internal_notes?: string | null;
};

export function leadFormOf(lead: LeadRow): LeadForm {
  return {
    company_name: lead.company_name ?? '',
    siret: lead.siret ?? '',
    address: lead.address ?? '',
    postal_code: lead.postal_code ?? '',
    city: lead.city ?? '',
    country: lead.country ?? '',
    contact_first_name: lead.contact_first_name ?? '',
    contact_last_name: lead.contact_last_name ?? '',
    contact_email: lead.contact_email ?? '',
    contact_phone: lead.contact_phone ?? '',
    erp: lead.erp ?? '',
    erp_other: lead.erp_other ?? '',
    onboarding_type: lead.onboarding_type ?? '',
    status: lead.status ?? '',
    internal_notes: lead.internal_notes ?? '',
  };
}

const NOT_NULL: readonly (keyof LeadForm)[] = [
  'company_name',
  'siret',
  'address',
  'postal_code',
  'city',
  'country',
  'contact_first_name',
  'contact_last_name',
  'contact_email',
  'contact_phone',
  'erp',
  'status',
];

/** The changes the form makes, trimmed — only what differs; a nameless company refused. */
export function leadEditPatch(lead: LeadRow, form: LeadForm): { patch: LeadPatch } | { error: string } {
  if (!form.company_name.trim()) return { error: "Le lead a besoin du nom de l'entreprise." };
  // Checked only when changed: an old malformed address never blocks another field's save.
  const email = form.contact_email.trim();
  if (email && email !== (lead.contact_email ?? '').trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email)) {
    return { error: 'Adresse mail illisible.' };
  }
  const initial = leadFormOf(lead);
  const patch: Record<string, string | null> = {};
  for (const key of Object.keys(form) as (keyof LeadForm)[]) {
    const value = form[key].trim();
    if (value === initial[key].trim()) continue;
    // Columns that hold text never take null; the others clear to null.
    patch[key] = NOT_NULL.includes(key) ? value : value || null;
  }
  return { patch: patch as LeadPatch };
}
